import moment from "moment";
import html2pdf from "html2pdf.js";
import { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";

import 'react-circular-progressbar/dist/styles.css';
import { URL as API_URL } from "../../../../env";
import { showMessage } from "../../../../config";

export default function SiderbarResearchAid({ company, tab, setTab, setOpenTabs, setTabName, setCompanyDashboardTab, isBackFitReport, setIsBackFitReport, title, reportData, onCloseReport }) {
    let user = useSelector(state => state.user);

    let [sidebarFixed, setSidebarFixed] = useState(false);
    let reportsNames = {
        'marketReport-researchaid': 'MarketOpporunity',
        'competitorReport-researchaid': 'CompetitorAnalysis',
        'routeToMarketReport-researchaid': 'RouteToMarket',
        'TCAR3-researchaid': 'TechAdvantage',
        'swotAnalysis-researchaid': 'SWOT',
        'insuranceGapAnalysis-researchaid': 'InsuranceGapAnalysis',
        'insurance-gap-analysis-report': 'InsuranceGapAnalysis'
    };

    useEffect(() => {
        let isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const handleSidebarStick = () => {
            if (isSafari && window.scrollY > 925) setSidebarFixed(true);
            else setSidebarFixed(false);
        };

        window.addEventListener('scroll', handleSidebarStick);
        return () => window.removeEventListener('scroll', handleSidebarStick);
    }, []);

    const convertImageToBase64 = async (imgUrl) => {
        try {
            let absoluteUrl = imgUrl;
            if (!imgUrl.startsWith('http') && !imgUrl.startsWith('//') && !imgUrl.startsWith('data:') && !imgUrl.startsWith('blob:')) {
                if (imgUrl.startsWith('/')) {
                    absoluteUrl = window.location.origin + imgUrl;
                } else {
                    absoluteUrl = new window.URL(imgUrl, window.location.href).href;
                }
            }

            const needsProxy = absoluteUrl.includes('s3') || absoluteUrl.includes('amazonaws') || absoluteUrl.includes('cloudfront');
            const fetchUrl = needsProxy
                ? `${API_URL}/api/company/pdf-proxy?url=${encodeURIComponent(absoluteUrl)}`
                : absoluteUrl;

            const response = await fetch(fetchUrl);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to base64:', imgUrl, error);
            return imgUrl;
        }
    };

    const convertAllImagesToBase64 = async (element) => {
        // Convert all img elements
        const images = element.querySelectorAll('img');
        const imagePromises = [];
        for (const img of images) {
            const src = img.src;
            if (src && (src.includes('s3') || src.includes('amazonaws') || src.includes('cloudfront') || src.startsWith('http'))) {
                imagePromises.push(convertImageToBase64(src).then(base64 => {
                    img.setAttribute('data-original-src', src);
                    img.src = base64;
                }));
            }
        }

        // Convert background images (check both computed styles and inline styles)
        const allElements = element.querySelectorAll('*');
        const backgroundPromises = [];
        allElements.forEach(el => {
            // Check computed style first
            let bgImage = window.getComputedStyle(el).backgroundImage;

            // Also check inline style if computed style is empty
            if ((!bgImage || bgImage === 'none') && el.style.backgroundImage) {
                bgImage = el.style.backgroundImage;
            }

            if (bgImage && bgImage !== 'none' && bgImage.includes('url(')) {
                const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
                if (urlMatch && urlMatch[1]) {
                    let bgUrl = urlMatch[1];

                    // Skip if already a data URL or blob URL
                    if (bgUrl.startsWith('data:') || bgUrl.startsWith('blob:')) {
                        // Already converted, skip
                        return;
                    }

                    // Convert all external and relative image URLs (including webpack-processed paths)
                    // This includes: http URLs, S3 URLs, and relative paths (webpack bundles)
                    const shouldConvert = bgUrl.startsWith('http') ||
                        bgUrl.startsWith('//') ||
                        bgUrl.startsWith('/') ||
                        bgUrl.includes('s3') ||
                        bgUrl.includes('amazonaws') ||
                        bgUrl.includes('cloudfront') ||
                        bgUrl.includes('static/');

                    if (shouldConvert) {
                        backgroundPromises.push(convertImageToBase64(bgUrl).then(base64 => {
                            const originalBg = el.style.backgroundImage || bgImage;
                            el.setAttribute('data-original-bg', originalBg);
                            el.style.backgroundImage = `url(${base64})`;
                        }).catch(err => {
                            console.warn('Failed to convert background image:', bgUrl, err);
                        }));
                    }
                }
            }
        });

        await Promise.all([...imagePromises, ...backgroundPromises]);
    };

    const restoreOriginalImages = (element) => {
        // Restore img elements
        const images = element.querySelectorAll('img[data-original-src]');
        for (const img of images) {
            img.src = img.getAttribute('data-original-src');
            img.removeAttribute('data-original-src');
        }

        // Restore background images
        const bgElements = element.querySelectorAll('[data-original-bg]');
        for (const el of bgElements) {
            el.style.backgroundImage = el.getAttribute('data-original-bg');
            el.removeAttribute('data-original-bg');
        }
    };

    const downloadPDF = async () => {
        if ((company['subscription'] != 'Pro' || user?.['role'] == 'Guest') && user['type'] !== 'External') return showMessage('Please upgrade to PRO to download report')
        try {
            const wrapper = document.getElementById("pdf");
            if (!wrapper) {
                console.error('PDF wrapper not found');
                return;
            }

            document.querySelectorAll(".oscy").forEach(el => el.classList.remove("h60vh"));
            document.querySelectorAll(".tableRow").forEach(el => el.classList.remove("mah40vh"));
            document.querySelectorAll(".tableRow").forEach(el => el.classList.add("mah100vh"));
            wrapper.classList.remove("gg2rem");
            for (const page of wrapper.children)
                page.classList.remove("shadow");
            for (const hideEl of wrapper.querySelectorAll(".hideOnDownload"))
                hideEl.classList.add("op0");

            // Wait for all images to load first
            const allImages = wrapper.querySelectorAll('img');
            const imageLoadPromises = Array.from(allImages).map(img => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = resolve; // Resolve even on error to not block
                    setTimeout(resolve, 2000); // Timeout after 2 seconds
                });
            });
            await Promise.all(imageLoadPromises);

            // Convert all images (including background images) to base64
            await convertAllImagesToBase64(wrapper);

            // Wait a bit for base64 images to be applied
            await new Promise(resolve => setTimeout(resolve, 300));

            const filename = [`DG-${reportsNames[tab]}-${company['cName'].substring(0, 7)}-${moment(Date.now()).format("ll")}`].join(" - ");
            const opt = {
                filename,
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'p' },
                html2canvas: {
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    scale: 2,
                    backgroundColor: '#ffffff',
                    removeContainer: true
                }
            };
            await html2pdf().set(opt).from(wrapper).save();

            // Restore original images after PDF generation
            restoreOriginalImages(wrapper);

            wrapper.classList.add("gg2rem");
            for (const page of wrapper.children)
                page.classList.add("shadow");
            for (const hideEl of wrapper.querySelectorAll(".hideOnDownload"))
                hideEl.classList.remove("op0");
            document.querySelectorAll(".oscy").forEach(el => el.classList.add("h60vh"));
            document.querySelectorAll(".tableRow").forEach(el => el.classList.add("mah40vh"));
            document.querySelectorAll(".tableRow").forEach(el => el.classList.remove("mah100vh"));
        } catch (e) {
            console.error('Error generating PDF:', e);
            showMessage('Error generating PDF. Please try again.');
        }
    };

    const handleCloseReport = async () => {
        // if (title) {
        //     setTab('repository')
        //     setCompanyDashboardTab('Dashboard')
        //     // setTimeout(() => {
        //     //     const targetElement = document.getElementById("D006");
        //     //     if (targetElement) {
        //     //         targetElement.scrollIntoView({
        //     //             behavior: 'smooth',
        //     //             block: 'start'
        //     //         });
        //     //     }
        //     // }, 500);
        //     return
        // }
        if (user['role'] == 'Investor' && user?.['companyId']?.['_id'] && isBackFitReport) {
            await setCompanyDashboardTab('Dashboard');
            setIsBackFitReport(false);
            setTab(`fit-report:${user?.['companyId']?.['_id']}`);
            setOpenTabs(new Set([`fit-report:${user?.['companyId']?.['_id']}`]));
            return;
        };
        if ((localStorage.getItem('isDiligencePack') && !isBackFitReport) || user?.['type'] == 'External') {
            await setCompanyDashboardTab('Dashboard');
            setTab('diligence-pack');
            setOpenTabs(new Set([`diligence-pack`]));
            localStorage.removeItem('isDiligencePack');
            return;
        };
        if (user['role'] == 'User' && isBackFitReport) {
            setIsBackFitReport(false);
            setTabName('');
            setCompanyDashboardTab('InvestorMatch')
            return;
        };
        await setTabName('');
        setCompanyDashboardTab('ResearchAid');
    };

    const handleCloseReportLink = () => {
        if (user['role'] == 'Investor' && user?.['companyId']?.['_id'] && isBackFitReport) return `/company/${company['_id']}#fit-report:${user?.['companyId']?.['_id']}`;
        else if ((localStorage.getItem('isDiligencePack') && !isBackFitReport) || user?.['type'] == 'External') return `/company/${company['_id']}#diligence-pack`;
        else return `/company/${company['_id']}`;
    };

    return (
        <>
            <div id="sidebar" style={{ width: "calc((100% - 4rem) / 5)" }} className={`w100 h100 bsbb dg gg1rem t0 sidebar-report ${sidebarFixed ? 'sidebar-fixed' : 'ps'}`}>
                <div className="bo45 bss bw1px br8px bg38">
                    <div className="df jcc aic fdc py1rem pb6rem">
                        <h3 className="c3 fs1-5rem fw500 ttu w100 tac mb1rem">{title ? title : "RESEARCHAID REPORT"}</h3>
                        {user['type'] !== 'External' &&
                            <button
                                onClick={() => handleCloseReport()}
                                className='close-report-btn-reaserch cp my1rem mxa bg1 bg4-hover c15 fs1rem c1-hover td250 fw300 bw1px bss bo4 br50px py0-5rem px3rem tac'
                                type="button">
                                <i className="fa fa-times-circle c4 c1-hover td250 mr0-5rem " /> Close Report
                            </button>
                        }
                        <button onClick={() => downloadPDF()} type="button" data-tooltip={company['subscription'] != 'Pro' ? 'Please upgrade to PRO to download report' : ''}
                            className={`df jcc aic mt0-5rem mxa shadow c1 br30px fs0-75rem py0-75rem px3rem fw600 ttu download-report-btn-reaserch ${((company['subscription'] != 'Pro' || user?.['role'] == 'Guest') && user['type'] !== 'External') ? ' bg3 cna op30' : ' bg3 bg4-hover cp'}`}>
                            <svg className="mr0-5rem" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.5703 13.4294H3.42745C2.66433 13.4294 1.94684 13.1322 1.40716 12.5925C0.867511 12.0529 0.570312 11.3354 0.570312 10.5722V9.85793C0.570312 9.46342 0.890092 9.14362 1.2846 9.14362C1.67911 9.14362 1.99889 9.46342 1.99889 9.85793V10.5722C1.99889 10.9538 2.1475 11.3126 2.41728 11.5824C2.68713 11.8522 3.04586 12.0008 3.42745 12.0008H10.5703C10.9519 12.0008 11.3106 11.8522 11.5804 11.5824C11.8502 11.3125 11.9989 10.9538 11.9989 10.5722V9.85793C11.9989 9.46342 12.3187 9.14362 12.7131 9.14362C13.1076 9.14362 13.4275 9.46342 13.4275 9.85793V10.5722C13.4275 11.3354 13.1303 12.0528 12.5906 12.5925C12.0509 13.1322 11.3334 13.4294 10.5703 13.4294ZM6.99887 10.5722C6.90006 10.5722 6.80599 10.5522 6.72041 10.5159C6.64057 10.4822 6.56554 10.4332 6.49989 10.3691C6.49986 10.369 6.49986 10.369 6.49984 10.369C6.49936 10.3686 6.49889 10.3681 6.49842 10.3676C6.49829 10.3675 6.49814 10.3673 6.49802 10.3672C6.49762 10.3669 6.49727 10.3665 6.49689 10.3661C6.49664 10.3659 6.49642 10.3657 6.49617 10.3654C6.49592 10.3651 6.4956 10.3648 6.49537 10.3646C6.49487 10.3641 6.49432 10.3636 6.49382 10.3631L3.63667 7.50589C3.35773 7.22696 3.35773 6.77469 3.63667 6.49573C3.9156 6.21679 4.3679 6.21677 4.64683 6.49573L6.28461 8.1335V1.28656C6.28458 0.892046 6.60436 0.572266 6.99887 0.572266C7.39338 0.572266 7.71319 0.892046 7.71319 1.28656V8.13348L9.35093 6.49573C9.62984 6.21679 10.0822 6.21679 10.3611 6.49573C10.64 6.77466 10.64 7.22696 10.3611 7.50589L7.50394 10.363C7.50344 10.3635 7.50289 10.3641 7.50239 10.3646C7.50212 10.3648 7.50182 10.3651 7.5016 10.3653C7.50135 10.3656 7.50112 10.3658 7.50087 10.3661C7.50052 10.3665 7.50012 10.3668 7.49977 10.3672C7.49965 10.3673 7.49948 10.3675 7.49935 10.3676C7.4989 10.368 7.49843 10.3685 7.49795 10.369C7.49793 10.369 7.49793 10.369 7.4979 10.369C7.49004 10.3767 7.48208 10.3841 7.47395 10.3913C7.41414 10.4447 7.34752 10.4863 7.27708 10.516C7.27683 10.5161 7.27663 10.5162 7.27638 10.5163C7.27611 10.5164 7.27588 10.5165 7.27561 10.5166C7.19047 10.5525 7.09701 10.5722 6.99887 10.5722Z" fill="white" />
                            </svg>
                            Download PDF
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}